/**
 * kit — shared primitives from the 5mi.uz design handoff.
 *
 * Visual building blocks: Logo (pentagon "5"), Hex tile, Avatar (initials),
 * Badge, Stat card, SectionHeader, Spark (mini line chart).
 * Icons come from lucide-react (stroke-based, matches the prototype's set).
 */

// Pentagon tile — the recurring "5mi" shape
export function Hex({ bg = 'var(--coral-soft)', fg = 'var(--coral)', size = 'md', icon: Icon, label, dot }) {
  const cls = size === 'sm' ? 'hex hex-sm' : size === 'lg' ? 'hex hex-lg' : 'hex'
  const isize = size === 'sm' ? 16 : size === 'lg' ? 28 : 22
  return (
    <div style={{ position: 'relative', width: 'fit-content' }}>
      <div className={cls} style={{ background: bg, color: fg }}>
        {label
          ? <span>{label}</span>
          : Icon && <Icon size={isize} color={fg} strokeWidth={2} />}
      </div>
      {dot && (
        <span style={{
          position: 'absolute', top: 4, right: 6, width: 8, height: 8,
          borderRadius: '50%', background: 'var(--coral)', boxShadow: '0 0 0 2px var(--surface)',
        }} />
      )}
    </div>
  )
}

// 5mi.uz logo — pentagon "5" + wordmark
export function Logo({ size = 28, color = 'var(--coral)', compact = false }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: size, height: size,
        clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)',
        background: color,
        color: '#fff', display: 'grid', placeItems: 'center',
        fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: size * 0.46,
        lineHeight: 1, paddingTop: size * 0.2,
      }}>5</div>
      {!compact && (
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 18, letterSpacing: '-0.02em', color: 'var(--ink)' }}>
          5mi<span style={{ color }}>.uz</span>
        </div>
      )}
    </div>
  )
}

// Avatar — initials on pastel
const AVATAR_PALETTE = [
  ['var(--coral-soft)', 'var(--coral)'],
  ['var(--mint-soft)', 'var(--mint)'],
  ['var(--lav-soft)', 'var(--lav)'],
  ['var(--butter-soft)', 'var(--butter)'],
  ['var(--sky-soft)', 'var(--sky)'],
  ['var(--pink-soft)', 'var(--pink)'],
]

export function Avatar({ name = '?', size = 36, idx }) {
  const safe = String(name || '?')
  const i = idx != null
    ? idx % AVATAR_PALETTE.length
    : Math.abs([...safe].reduce((a, c) => a + c.charCodeAt(0), 0)) % AVATAR_PALETTE.length
  const [bg, fg] = AVATAR_PALETTE[i]
  const initials = safe.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase() || '?'
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: bg, color: fg,
      display: 'grid', placeItems: 'center',
      fontWeight: 700, fontSize: size * 0.4,
      flex: '0 0 auto',
    }}>{initials}</div>
  )
}

// Status badge — tone palette from the design
const BADGE_TONES = {
  coral:   { bg: 'var(--coral-soft)',  fg: 'var(--coral)' },
  mint:    { bg: 'var(--mint-soft)',   fg: 'var(--mint)' },
  lav:     { bg: 'var(--lav-soft)',    fg: 'var(--lav)' },
  butter:  { bg: 'var(--butter-soft)', fg: 'var(--butter)' },
  sky:     { bg: 'var(--sky-soft)',    fg: 'var(--sky)' },
  pink:    { bg: 'var(--pink-soft)',   fg: 'var(--pink)' },
  muted:   { bg: 'var(--bg-soft)',     fg: 'var(--ink-3)' },
  success: { bg: 'var(--success-soft)', fg: 'var(--success)' },
  danger:  { bg: 'var(--danger-soft)', fg: 'var(--danger)' },
}

export function Badge({ tone = 'coral', children, dot, style }) {
  const t = BADGE_TONES[tone] || BADGE_TONES.coral
  return (
    <span className="badge" style={{ background: t.bg, color: t.fg, ...style }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: t.fg }} />}
      {children}
    </span>
  )
}

// Mini stat card
export function Stat({ icon, tone = 'coral', label, value, delta }) {
  return (
    <div className="card" style={{ padding: 18, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <Hex size="sm" icon={icon} bg={`var(--${tone}-soft)`} fg={`var(--${tone})`} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="t-xs t-mut fw-6">{label}</div>
        <div className="h-display" style={{ fontSize: 24, lineHeight: 1.1, marginTop: 4 }}>{value}</div>
        {delta && (
          <div className="t-xs" style={{ marginTop: 4, color: String(delta).startsWith('+') ? 'var(--success)' : 'var(--danger)' }}>
            {delta}
          </div>
        )}
      </div>
    </div>
  )
}

// Section header with coral dot
export function SectionHeader({ title, subtitle, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
      <div>
        <h2 className="h-display dot" style={{ margin: 0, fontSize: 22 }}>{title}</h2>
        {subtitle && <div className="t-sm t-mut" style={{ marginTop: 4 }}>{subtitle}</div>}
      </div>
      {right}
    </div>
  )
}

// Toy line chart for stat rows
export function Spark({ color = 'var(--coral)', points = [4, 6, 5, 8, 7, 10, 9, 12], width = 120, height = 32 }) {
  const max = Math.max(...points), min = Math.min(...points)
  const dx = width / (points.length - 1)
  const pts = points.map((p, i) => `${i * dx},${height - ((p - min) / (max - min || 1)) * (height - 4) - 2}`).join(' ')
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
